import PropTypes from 'prop-types';
import {
  Text,
  Box,
  Textarea,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
  Spacer,
  Switch,
  VStack,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';

import ReactQuillEditor from 'components/common/ReactQuillEditor';

import { useEffect, useState } from 'react';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import { calculateContentSize } from 'utils/react-quill/quillConfig';
/* eslint-enable import/no-unresolved */

function EssayInput({ question, onUpdate }) {
  const [newQuestion, setNewQuestion] = useState({
    id: question.id,
    tempId: question.tempId,
    questionHtml: question.questionHtml,
    questionTypeId: question.questionTypeId,
    multipleChoiceChoices: question.multipleChoiceChoices || [],
    multipleChoiceAnswers: question.multipleChoiceAnswers || [],
    trueOrFalseAnswer: question.trueOrFalseAnswer || true,
    explanation: question.explanation || '',
    mark: question.mark,
    allowPartialPoints: question.allowPartialPoints,
    errors: question.errors,
    autoCheckInstruction: question.autoCheckInstruction || '',
    isAllowedAutoCheck: question.isAllowedAutoCheck ?? true,
  });

  // Initial validation on mount and when question state changes
  useEffect(() => {
    const errors = [];

    // Question text required
    if (
      !newQuestion.questionHtml ||
      newQuestion.questionHtml.trim() === '' ||
      newQuestion.questionHtml === '<p><br></p>'
    )
      errors.push('Question cannot be blank. ');

    // Question content size validation
    if (newQuestion.questionHtml) {
      const questionSize = calculateContentSize(newQuestion.questionHtml);
      if (questionSize > 3) {
        errors.push('Question content size exceeds 3MB limit. ');
      }
    }

    if (
      newQuestion.isAllowedAutoCheck &&
      newQuestion.autoCheckInstruction.trim() === ''
    )
      errors.push('Auto-check Instruction cannot be blank. ');
    if (
      newQuestion.isAllowedAutoCheck &&
      (typeof newQuestion.autoCheckInstruction !== 'string' ||
        newQuestion.autoCheckInstruction.trim().length < 1 ||
        newQuestion.autoCheckInstruction.trim().length > 1000)
    ) {
      errors.push(
        'Auto-check Instruction must be between 1 and 1000 characters.'
      );
    }

    // Mark must be positive
    if (typeof newQuestion.mark !== 'number' || newQuestion.mark <= 0) {
      errors.push('Mark must be a positive number. ');
    }

    setNewQuestion((prev) => ({
      ...prev,
      errors,
    }));

    if (onUpdate) {
      onUpdate({
        ...newQuestion,
        errors,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newQuestion.questionHtml,
    newQuestion.autoCheckInstruction,
    newQuestion.isAllowedAutoCheck,
    newQuestion.mark,
  ]);

  // Generic field change handler
  const handleFieldChange = (field, value) => {
    let newErrors = [...newQuestion.errors];

    // Handle auto-check instruction validation
    if (field === 'autoCheckInstruction') {
      const autoCheckError = 'Auto-check Instruction cannot be blank. ';
      const lengthError =
        'Auto-check Instruction must be between 1 and 1000 characters.';

      // Remove existing auto-check related errors
      newErrors = newErrors.filter(
        (e) => e !== autoCheckError && e !== lengthError
      );

      // Add validation if auto-check is enabled
      if (newQuestion.isAllowedAutoCheck) {
        if (!value || value.trim() === '') {
          newErrors.push(autoCheckError);
        } else if (value.trim().length < 1 || value.trim().length > 1000) {
          newErrors.push(lengthError);
        }
      }
    }

    // Handle mark validation
    if (field === 'mark') {
      const markError = 'Mark must be a positive number. ';
      newErrors = newErrors.filter((e) => e !== markError);

      const numValue = parseFloat(value);
      if (Number.isNaN(numValue) || numValue <= 0) {
        newErrors.push(markError);
      }
    }

    const updatedQuestion = {
      ...newQuestion,
      [field]: field === 'mark' ? parseFloat(value) || 0 : value,
      errors: newErrors,
    };

    setNewQuestion(updatedQuestion);

    // Notify parent component of the change
    if (onUpdate) {
      onUpdate(updatedQuestion);
    }
  };

  // For ReactQuill editor
  const handleQuestionChange = (content) => {
    let newErrors = [...newQuestion.errors];
    const questionError = 'Question cannot be blank. ';
    const sizeError = 'Question content size exceeds 3MB limit. ';

    // Check if content is empty
    if (!content || content.trim() === '' || content === '<p><br></p>') {
      if (!newErrors.includes(questionError)) {
        newErrors.push(questionError);
      }
    } else {
      newErrors = newErrors.filter((e) => e !== questionError);
    }

    // Check content size
    if (content) {
      const contentSize = calculateContentSize(content);
      if (contentSize > 3) {
        if (!newErrors.includes(sizeError)) {
          newErrors.push(sizeError);
        }
      } else {
        newErrors = newErrors.filter((e) => e !== sizeError);
      }
    } else {
      newErrors = newErrors.filter((e) => e !== sizeError);
    }

    const updatedQuestion = {
      ...newQuestion,
      questionHtml: content,
      errors: newErrors,
    };

    setNewQuestion(updatedQuestion);

    // Notify parent component of the change
    if (onUpdate) {
      onUpdate(updatedQuestion);
    }
  };

  return (
    <VStack gap={4} align="stretch">
      <Flex align="center">
        {newQuestion.errors.length > 0 && (
          <Box color="red.500">
            <UnorderedList>
              {newQuestion.errors.map((err) => (
                <ListItem key={err}>{err}</ListItem>
              ))}
            </UnorderedList>
          </Box>
        )}
      </Flex>

      <FormikFormControl label="Question">
        <ReactQuillEditor
          value={newQuestion.questionHtml}
          onChange={handleQuestionChange}
          placeholder="Enter question text..."
          maxSizeInMB={3}
        />
      </FormikFormControl>

      <Flex align="center">
        <Box mr={3}>
          <Text>Mark</Text>
        </Box>
        <Box>
          <NumberInput
            min={0}
            max={100}
            w="100px"
            onChange={(value) => handleFieldChange('mark', value)}
            value={newQuestion.mark}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </Box>
        <Spacer />
        <Box>
          <Checkbox
            size="lg"
            mr={5}
            onChange={(e) =>
              handleFieldChange('allowPartialPoints', e.target.checked)
            }
            isChecked={newQuestion.allowPartialPoints}
          >
            Allow partial points
          </Checkbox>
        </Box>
      </Flex>

      <FormikFormControl label="Explanation">
        <Textarea
          value={newQuestion.explanation}
          onChange={(e) => handleFieldChange('explanation', e.target.value)}
        />
      </FormikFormControl>

      <FormikFormControl
        label="Allow AI To Check Answer?"
        helperText="Enable AI to automatically score this answer based on the provided instructions."
      >
        <Switch
          id="email-alerts"
          isChecked={newQuestion.isAllowedAutoCheck}
          onChange={() =>
            handleFieldChange(
              'isAllowedAutoCheck',
              !newQuestion.isAllowedAutoCheck
            )
          }
        />
      </FormikFormControl>

      {newQuestion.isAllowedAutoCheck && (
        <FormikFormControl
          label="Auto-check Instruction *"
          helperText="Describe the criteria or guidelines the AI should use to evaluate the answer."
        >
          <Textarea
            value={newQuestion.autoCheckInstruction}
            onChange={(e) =>
              handleFieldChange('autoCheckInstruction', e.target.value)
            }
          />
        </FormikFormControl>
      )}
    </VStack>
  );
}

EssayInput.propTypes = {
  question: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default EssayInput;
